// src/helpers/rutaPagoVentas.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () => localStorage.getItem("token") || "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const getId = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return clean(v._id ?? v.id ?? v.value ?? "");
  }

  return clean(v);
};

const toNumberMinZero = (v, def = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return def;
  return n < 0 ? 0 : n;
};

const normalizeMedioPago = (v) => {
  const s = clean(v).toUpperCase();

  if (s === "SELLE") return "ZELLE";

  return s;
};

const normalizeEstadoAcreditacion = (v) => {
  const s = clean(v).toUpperCase();

  if (!s) return "";
  if (s === "PAGADO") return "ACREDITADO";

  return s;
};

const unwrapPagoVenta = (data) => {
  if (!data) return null;

  if (data?.pago) return data.pago;
  if (data?.pagoVenta) return data.pagoVenta;
  if (data?.data) return data.data;

  return data;
};

const unwrapPagosVenta = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.pagos)) return data.pagos;
  if (Array.isArray(data?.pagosVenta)) return data.pagosVenta;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

export const normalizarPagoVentaPayload = (payload = {}) => {
  const src = payload || {};

  const medioPago = normalizeMedioPago(src.medioPago);


  const esFinanciamientoCasa =
  medioPago === "FINANCIAMIENTO CON LA CASA";

  const usaResponsable = [
    "CREDITO FINANCIERA",
    "CREDITO",
    "DEBITO",
  ].includes(medioPago);

  const tieneFondeoExplicito =
    Object.prototype.hasOwnProperty.call(
      src,
      "fondeoFinancieraUSD"
    ) ||
    Object.prototype.hasOwnProperty.call(
      src,
      "fondeo"
    );

  

  const data = {
    venta: getId(src.venta ?? src.ventaId),
    ventaId: getId(src.venta ?? src.ventaId),

    medioPago,

    montoUSD: toNumberMinZero(src.montoUSD ?? src.monto, 0),

    cuotasFinanciacion:
      medioPago === "CREDITO FINANCIERA"
        ? toNumberMinZero(src.cuotasFinanciacion ?? src.cuotas, 0)
        : 0,

    feePorcentaje: toNumberMinZero(src.feePorcentaje, 0),
    feeMontoUSD: toNumberMinZero(src.feeMontoUSD, 0),
    montoFinancieraUSD: toNumberMinZero(src.montoFinancieraUSD, 0),
    totalCobrarClienteUSD: toNumberMinZero(src.totalCobrarClienteUSD, 0),

    financiera:
      medioPago === "CREDITO FINANCIERA"
        ? getId(src.financiera ?? src.financieraId)
        : null,

    clienteFinanciacion:
      usaResponsable
        ? getId(
            src.clienteFinanciacion ??
              src.clienteFinanciacionId
          )
        : null,

    estadoAcreditacion: normalizeEstadoAcreditacion(
      src.estadoAcreditacion ||
        (
          medioPago === "CREDITO FINANCIERA" ||
          esFinanciamientoCasa
            ? "PENDIENTE"
            : "ACREDITADO"
        )
    ),

    fechaPago: src.fechaPago || undefined,
    fechaAcreditacion: src.fechaAcreditacion || undefined,

    observaciones: clean(src.observaciones).toUpperCase(),

    activo: src.activo ?? true,
  };

    /*
    FONDEO INVERSO

    Lo enviamos solamente cuando el componente
    lo proporciona expresamente.

    MUY IMPORTANTE:
    no mandar 0 automáticamente, porque un PUT
    de un pago histórico debe seguir siendo
    reconocido como histórico por el backend.
  */
  if (
    medioPago === "CREDITO FINANCIERA" &&
    tieneFondeoExplicito
  ) {
    data.fondeoFinancieraUSD =
      toNumberMinZero(
        src.fondeoFinancieraUSD ??
          src.fondeo,
        0
      );
  }

  if (!data.venta) {
    delete data.venta;
    delete data.ventaId;
  }

  if (!data.fechaPago) {
    delete data.fechaPago;
  }

  if (!data.fechaAcreditacion) {
    delete data.fechaAcreditacion;
  }

  if (
    data.medioPago !==
    "CREDITO FINANCIERA"
  ) {
    data.financiera = null;

    if (!usaResponsable) {
      data.clienteFinanciacion = null;
    }

    data.estadoAcreditacion =
      esFinanciamientoCasa
        ? "PENDIENTE"
        : "ACREDITADO";

    if (esFinanciamientoCasa) {
      delete data.fechaAcreditacion;
    }
  }

  if (src.contratoFirmado !== undefined) {
    data.contratoFirmado = Boolean(src.contratoFirmado);
  }

  if (
    data.medioPago !==
    "CREDITO FINANCIERA"
  ) {
    data.cuotasFinanciacion = 0;

    /*
      Solo las financieras utilizan
      montoFinancieraUSD.
    */
    data.montoFinancieraUSD = 0;

    /*
      TARJETA DE CRÉDITO conserva
      su fee del 3% y el total que
      viene calculado desde el componente.

      EFECTIVO / ZELLE / DÉBITO
      no llevan fee.
    */
    if (
      data.medioPago !==
      "CREDITO"
    ) {
      data.feePorcentaje = 0;
      data.feeMontoUSD = 0;

      data.totalCobrarClienteUSD =
        data.montoUSD;
    }
  }

  return data;
};

/* ------------------------------ PAGO VENTAS ------------------------------ */

// GET /pagoventas/venta/:ventaId
export async function getPagosVenta(ventaId, { signal } = {}) {
  const id = getId(ventaId);

  if (!id) {
    return {
      pagos: [],
      total: 0,
      venta: null,
      resumenPago: null,
    };
  }

  const data = await req("GET", p(`pagoventas/venta/${id}`), {
    signal,
  });

  const pagos = unwrapPagosVenta(data);

  return {
    pagos,
    total: Number(data?.total ?? pagos.length ?? 0),
    venta: data?.venta || null,
    resumenPago: data?.resumenPago || null,
  };
}

// GET /pagoventas/:id
export async function getPagoVentaById(id, { signal } = {}) {
  const data = await req("GET", p(`pagoventas/${id}`), { signal });
  return unwrapPagoVenta(data);
}

// POST /pagoventas
export async function postPagoVenta(payload, { signal } = {}) {
  const data = normalizarPagoVentaPayload(payload);

  if (debug) {
    console.log("🟢 postPagoVenta PAYLOAD:", data);
  }

  return req("POST", p("pagoventas"), {
    data,
    signal,
  });
}

// PUT /pagoventas/:id
export async function putPagoVenta(id, payload, { signal } = {}) {
  const data = normalizarPagoVentaPayload(payload);

  if (debug) {
    console.log("🟣 putPagoVenta ID:", id);
    console.log("🟣 putPagoVenta ORIGINAL:", payload);
    console.log("🟢 putPagoVenta NORMALIZADO:", data);
    console.log("🌐 putPagoVenta URL:", `${BASE_URL}${p(`pagoventas/${id}`)}`);
  }

  return req("PUT", p(`pagoventas/${id}`), {
    data,
    signal,
  });
}

// PUT /pagoventas/:id/acreditar
export async function acreditarPagoVenta(id, { signal } = {}) {
  return req("PUT", p(`pagoventas/${id}/acreditar`), {
    signal,
  });
}

// DELETE /pagoventas/:id
export async function deletePagoVenta(id, { signal } = {}) {
  return req("DELETE", p(`pagoventas/${id}`), {
    signal,
  });
}

/* ------------------------------ CONSTANTES ------------------------------ */

export const MEDIOS_PAGO_VENTA = [
  "EFECTIVO EN TIENDA",
  "ZELLE",
  "DEBITO",
  "CREDITO",
  "CREDITO FINANCIERA",
  "FINANCIAMIENTO CON LA CASA",
];

export const ESTADOS_ACREDITACION_PAGO = [
  "PENDIENTE",
  "ACREDITADO",
  "ANULADO",
];

export async function marcarContratoFirmadoPagoVenta(
  id,
  contratoFirmado = true,
  { signal } = {}
) {
  return req("PUT", p(`pagoventas/${id}/firmado`), {
    data: { contratoFirmado },
    signal,
  });
}